import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";

type ConfirmDialogProps = {
  title: ReactNode;
  description?: ReactNode;
  confirmLabel: ReactNode;
  danger?: boolean;
  /** Keeps the confirm button disabled (e.g. until a required field is filled). */
  confirmDisabled?: boolean;
  /** Error text shown when onConfirm fails. */
  errorText?: (err: unknown) => ReactNode;
  onConfirm: () => Promise<unknown>;
  onClose: () => void;
  children?: ReactNode;
};

/** Asks before a destructive or important action; closes after success. */
export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  danger,
  confirmDisabled,
  errorText,
  onConfirm,
  onClose,
  children,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const confirm = async () => {
    setPending(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={title} description={description} className="w-[min(92vw,440px)]">
        <div className="flex flex-col gap-4 p-5">
          {children}
          {error !== null && (
            <Alert variant="danger">{errorText ? errorText(error) : t("errors.generic")}</Alert>
          )}
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button variant="secondary">{t("common.cancel")}</Button>
            </DialogClose>
            <Button
              variant={danger ? "danger" : "primary"}
              disabled={pending || confirmDisabled}
              onClick={() => void confirm()}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
